import { useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ROLE_LABEL, ago, scopeLabel } from "@/lib/perms";
import { ScopePicker } from "@/components/ScopePicker";

const roleOptions = (myRole) => (myRole === "owner" ? ["admin", "editor", "viewer"] : ["editor", "viewer"]);

const MemberRow = ({ m, team, worldId, pages, reload }) => {
  const [role, setRole] = useState(m.role);
  const [scope, setScope] = useState(m.scope);
  const locked = team.my_role !== "owner" && m.role === "admin";
  const dirty = role !== m.role || JSON.stringify(scope) !== JSON.stringify(m.scope);
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <li className="py-4 space-y-2" data-testid={`member-${m.user_id}`}>
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-slate-200 truncate">{m.name || m.email}</p>
          <p className="text-[11px] text-slate-500 truncate">{m.email} · joined {ago(m.joined_at)}{m.last_active ? ` · active ${ago(m.last_active)}` : ""}</p>
        </div>
        {locked ? <span className="text-xs font-code text-amber-300">{ROLE_LABEL[m.role]}</span> : (
          <select value={role} onChange={(e) => setRole(e.target.value)} className="h-8 rounded-md bg-inset border border-line text-xs text-slate-200 px-2" data-testid={`member-role-${m.user_id}`}>
            {roleOptions(team.my_role).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
          </select>
        )}
        {!locked && <button onClick={() => run(() => api.delete(`/worlds/${worldId}/members/${m.user_id}`), "Member removed")} className="p-1.5 text-slate-500 hover:text-rose-400" data-testid={`member-remove-${m.user_id}`}><Trash2 className="w-4 h-4" /></button>}
      </div>
      {role === "editor" && !locked ? <ScopePicker value={scope} onChange={setScope} pages={pages} /> : role === "editor" && <p className="text-[11px] text-slate-500">Can edit: {scopeLabel(m.scope, pages)}</p>}
      {dirty && <button onClick={() => run(() => api.put(`/worlds/${worldId}/members/${m.user_id}`, { role, scope }), "Access updated")} className="text-xs rounded-full bg-amber-500 text-onaccent font-semibold px-3 py-1" data-testid={`member-save-${m.user_id}`}>Save access</button>}
    </li>
  );
};

export const TeamMembers = ({ team, worldId, pages, reload }) => (
  <ul className="divide-y divide-line" data-testid="team-members">
    <li className="py-4 flex items-center gap-3">
      <div className="flex-1 min-w-0"><p className="text-sm text-slate-200 truncate">{team.owner.name || team.owner.email}</p><p className="text-[11px] text-slate-500 truncate">{team.owner.email}</p></div>
      <span className="text-xs font-code text-amber-300">Owner</span>
    </li>
    {team.members.map((m) => <MemberRow key={`${m.user_id}-${m.role}-${JSON.stringify(m.scope)}`} m={m} team={team} worldId={worldId} pages={pages} reload={reload} />)}
    {team.members.length === 0 && <li className="py-6 text-sm text-slate-500">No one else yet. Invite a co-author from the Invites tab.</li>}
  </ul>
);
